import React, { useState, useEffect, ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { DATE_FORMAT, passlyFormatDate, TIME_DATE_FORMAT } from '@app/helpers';
import { useTypedTranslation } from '@app/hooks';
import { Link, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import { IActiveFilter, IActiveFilterNew, IDotMenu, IValueSuggestionsForFilter, OrderType, TranslationsTypes } from '@app/models';
import {PageLoader, PasslyCheckbox, PasslyInfiniteScroll, PasslyTextField} from '@app/components';
import { PasslyTableSortLabel, PasslyTooltip } from '@app/componentsV2';
import { TableCheckboxMenu } from '@app/components/TableITC/components';
import Icon from '@app/components/Icon';
import { resetState, setActiveFilters } from '@app/store/actions';
import FilterBar from '@app/components/TableITC/components/filterBar.component';

interface ITableHeadCell {
    id: string;
    label: TranslationsTypes;
    type?: string;
    node?: ReactNode;
    style?: any;
    className?: string;
    direction?: OrderType;
    sortable?: boolean;
    hidden?: boolean;
    width?: string;
    hideSorting?: boolean;
    headerTooltipText?: TranslationsTypes;

    // for select identities
    displayValueFromOrg?: boolean;
    searchable?: boolean;
    countable?: boolean;
    expandAttribute?: string;

    // filtering
    suggestionsForFilter?: IValueSuggestionsForFilter[];
    filterType?: string;
    defaultSearchBy?: string;
}

interface IProps {
    headerItems: ITableHeadCell[];
    rows: any;
    setRows?: Function;
    rowsCount: number;
    onFetch: Function;
    resetLocal?: number;
    emptyListHelper?: string;
    showRowsNumbers?: boolean;

    order?: OrderType;
    orderBy?: string;
    handleOrderChange?: (property: string) => () => void;

    filterBy?: string;
    withCheckboxes?: boolean;
    height?: string;
    className?: string;
    fixedHeader?: boolean;
    actionMenuItems?: IDotMenu[];
    loadingData?: boolean;

    /*filter*/
    searchValue?: string;
    setSearchValue?: Function;
    setFilters?: (filters: IActiveFilter[]) => void;
    hideFilter?: boolean;
    hideCounter?: boolean;
    /*filter end*/

    /*row menu*/
    editRow?: (event: React.MouseEvent<HTMLElement>) => void;
    deleteRow?: (event: React.MouseEvent<HTMLElement>) => void;
    openDetails?: (row: any) => () => void;
    openInNewTab?: (row: any) => () => void;
    /*row menu end*/

    hideCheckAllCheckbox?: boolean;
}

export const PasslyTable = ({
    rows,
    setRows,
    showRowsNumbers,
    searchValue,
    setSearchValue,
    setFilters,
    hideFilter,
    hideCounter,
    order,
    orderBy,
    handleOrderChange,
    headerItems,
    withCheckboxes,
    rowsCount,
    resetLocal,
    height,
    emptyListHelper,
    className,
    onFetch,
    fixedHeader,
    actionMenuItems,
    loadingData,
    editRow,
    deleteRow,
    openDetails,
    openInNewTab,
    hideCheckAllCheckbox
}: IProps) => {
    const { translation } = useTypedTranslation();
    const [selectAll, setSelectAll] = useState(false);
    const [selectedItemsCount, setSelectedItemsCount] = useState(0);
    const [partiallySelected, setPartiallySelected] = useState(false);
    const [countBeforeFiltering, setCountBeforeFiltering] = useState(0);
    const [currentCountForFilter, setCurrentCountForFilter] = useState(0);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [filterItems, setFilterItems] = useState<IActiveFilterNew[]>([]);
    const [defaultSearchBy, setDefaultSearchBy] = useState('');

    const dispatch = useDispatch();

    useEffect(() => {
        if (isFirstLoad && rowsCount) {
            setCountBeforeFiltering(rowsCount);
            setIsFirstLoad(false);
        }

        setCurrentCountForFilter(rowsCount);
    }, [rowsCount]);

    useEffect(() => {
        let enabledRows = rows.filter((row: any) => !row.checkboxDisabled);
        let checkedRows = enabledRows.filter((row: any) => row.checked);
        let allRowsSelected = checkedRows.length > 0 && checkedRows.length === enabledRows.length;
        let rowsPartiallySelected = checkedRows.length > 0 && checkedRows.length !== enabledRows.length;

        setSelectAll(allRowsSelected);
        setSelectedItemsCount(checkedRows.length);
        setPartiallySelected(rowsPartiallySelected);
    }, [rows]);

    useEffect(() => {
        const filterItemsNew: IActiveFilterNew[] = [];

        headerItems.forEach((headerItems) => {
            if (headerItems.filterType) {
                filterItemsNew.push({
                    type: headerItems.filterType,
                    value: '',
                    label: headerItems.label,
                    filterBy: headerItems.id,
                    suggestionsForFilter: headerItems.suggestionsForFilter
                });

                if (headerItems?.defaultSearchBy) {
                    setDefaultSearchBy(headerItems.defaultSearchBy);
                }
            }
        });

        setFilterItems(filterItemsNew);
    }, [headerItems]);

    const setFiltersDefault = (filters: IActiveFilter[]) => {
        dispatch(resetState());
        dispatch(setActiveFilters(filters));
    };

    const handleSetFilters = (filters: IActiveFilter[]) => {
        setFilters ? setFilters(filters) : setFiltersDefault(filters);
    };

    const getEmptyList = () => {
        return (
            <div className='passlyTable__empty'>
                <div className='passlyTable__empty-icon'>
                    <Icon icon='itemsNotFoundIco' />
                </div>
                <div dangerouslySetInnerHTML={{ __html: emptyListHelper ? emptyListHelper : translation('EMPTY_TABLE_DEFAULT') }} />
            </div>
        );
    };

    const updateAllCheckboxes = (checked: boolean, e: any) => {
        let newRows = [...rows];

        setSelectAll(checked);

        if (e.target) {
            newRows.forEach((newItem) => {
                if (!newItem.checkboxDisabled) {
                    newItem.checked = checked;
                }
            });

            setRows && setRows(newRows);
        }
    };

    const onCheckboxChange = (index: string) => (checked: boolean) => {
        const rowId: number = Number(index);
        let newRows = [...rows];

        newRows[rowId].checked = checked;
        setRows && setRows(newRows);
    };

    const getCheckboxItem = (row: any, index: string) => {
        return withCheckboxes ? (
            <TableCell>
                <PasslyCheckbox
                    name={row.id}
                    className='table__checkbox table__checkbox-body'
                    checked={row.checked}
                    onChange={onCheckboxChange(index)}
                    label=''
                    disabled={row.checkboxDisabled}
                />
            </TableCell>
        ) : null;
    };

    const getRowsNumbersHeaderCell = () => {
        return withCheckboxes ? <TableCell style={{ width: 80 }}>{translation('ROW')}</TableCell> : null;
    };

    const getRowsNumbersCell = (index: string) => {
        return withCheckboxes ? <TableCell>{index + 1}</TableCell> : null;
    };

    const getCheckboxAllItem = () => {
        return withCheckboxes && !hideCheckAllCheckbox ? (
            <TableCell className='passlyTable__checkbox-cell'>
                <TableCheckboxMenu
                    partiallySelected={partiallySelected}
                    selectAll={selectAll}
                    selectedItemsCount={String(selectedItemsCount)}
                    actionMenuItems={actionMenuItems}
                >
                    <PasslyCheckbox
                        name='selectall'
                        className='table__checkbox'
                        checked={partiallySelected ? 'mixed' : selectAll}
                        onChange={updateAllCheckboxes}
                        label=''
                    />
                </TableCheckboxMenu>
            </TableCell>
        ) : hideCheckAllCheckbox ? (
            <TableCell className='passlyTable__checkbox-cell' />
        ) : null;
    };

    const getHeaderSortLabel = (headCell: ITableHeadCell) => {
        return headCell.hideSorting || !handleOrderChange ? (
            translation(headCell.label)
        ) : (
            <PasslyTableSortLabel
                id={headCell.id}
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={handleOrderChange(headCell.id)}
            >
                {translation(headCell.label)}
            </PasslyTableSortLabel>
        );
    };

    const getHeaderItems = () => {
        return (
            <TableRow>
                {getCheckboxAllItem()}
                {showRowsNumbers ? getRowsNumbersHeaderCell() : null}

                {headerItems.map((headCell: ITableHeadCell) => {
                    return !headCell.hidden ? (
                        <TableCell key={headCell.id} style={headCell.width ? { width: headCell.width } : {}}>
                            {headCell.headerTooltipText ? (
                                <PasslyTooltip title={translation(headCell.headerTooltipText)}>
                                    <span>{headCell.label ? getHeaderSortLabel(headCell) : ''}</span>
                                </PasslyTooltip>
                            ) : headCell.label ? (
                                getHeaderSortLabel(headCell)
                            ) : (
                                ''
                            )}
                        </TableCell>
                    ) : null;
                })}

                {editRow || deleteRow || openDetails || openInNewTab ? <TableCell className='passlyTable__row-menu'>&nbsp;</TableCell> : null}
            </TableRow>
        );
    };

    const getStatusColor = (value: string) => {
        switch (value) {
            case 'Active':
            case 'Available':
                return 'green';
            default:
                return 'grey';
        }
    };

    const changeDisabledToInactive = (value: string) => {
        return value === 'Disabled' ? 'Inactive' : value;
    };

    const getRowMenuCell = (row: any) => {
        return editRow || deleteRow || openDetails || openInNewTab ? (
            <TableCell className='passlyTable__row-menu'>
                <div>
                    {openDetails ? (
                        <PasslyTooltip title={translation('VIEW_DETAILS')}>
                            <i data-id={row.id} onClick={openDetails(row)} className='far fa-info-circle' />
                        </PasslyTooltip>
                    ) : null}
                    {openInNewTab ? (
                        <PasslyTooltip title={translation('OPEN_IN_NEW_TAB')}>
                            <i data-id={row.id} onClick={openInNewTab(row)} className='far fa-external-link' />
                        </PasslyTooltip>
                    ) : null}
                    {editRow && !row.cantEdit ? (
                        <PasslyTooltip title={translation('EDIT')}>
                            <i data-id={row.id} onClick={editRow} className='fal fa-pencil' />
                        </PasslyTooltip>
                    ) : null}
                    {deleteRow && !row.cantDelete ? (
                        <PasslyTooltip title={translation('DELETE')}>
                            <i data-id={row.id} data-isoffice={row.isOffice365App} onClick={deleteRow} className='far fa-trash-alt' />
                        </PasslyTooltip>
                    ) : null}
                </div>
            </TableCell>
        ) : null;
    };

    const getCells = (item: any, index: string) => {
        return headerItems.map((headerItem: any, cellIndex: number) => {
            if (headerItem.hidden) {
                return null;
            }

            const cellValue = item[headerItem.id];

            switch (headerItem.type) {
                case 'link':
                    return (
                        <TableCell key={cellIndex}>
                            <Link data-id={item.id} onClick={headerItem.onClick}>
                                {cellValue}
                            </Link>
                        </TableCell>
                    );
                case 'linkWithTooltip':
                    return (
                        <TableCell key={cellIndex}>
                            <PasslyTooltip title={cellValue}>
                                <Link data-id={item.id} onClick={headerItem.onClick}>
                                    {cellValue}
                                </Link>
                            </PasslyTooltip>
                        </TableCell>
                    );
                case 'linkWithData':
                    return (
                        <TableCell key={cellIndex}>
                            <Link data-id={item.id} onClick={headerItem.onClick(item)}>
                                {cellValue}
                            </Link>
                        </TableCell>
                    );
                case 'string':
                    return (
                        <TableCell onClick={item.onClick} key={cellIndex}>
                            {cellValue}
                        </TableCell>
                    );
                case 'withTooltip':
                    return (
                        <PasslyTooltip title={cellValue} key={cellIndex}>
                            <TableCell onClick={item.onClick}>{cellValue}</TableCell>
                        </PasslyTooltip>
                    );
                case 'number':
                    return (
                        <TableCell onClick={item.onClick} className='passlyTable__cell-number' key={cellIndex}>
                            {cellValue}
                        </TableCell>
                    );
                case 'date':
                    return (
                        <TableCell onClick={item.onClick} key={cellIndex}>
                            {cellValue ? passlyFormatDate(cellValue, DATE_FORMAT) : translation('DATE_NEVER')}
                        </TableCell>
                    );
                case 'dateTime':
                    return (
                        <TableCell onClick={item.onClick} key={cellIndex}>
                            {cellValue ? passlyFormatDate(cellValue, TIME_DATE_FORMAT) : translation('DATE_NEVER')}
                        </TableCell>
                    );
                case 'status':
                    return (
                        <TableCell onClick={item.onClick} key={cellIndex}>
                            <div className={`passlyTable__status-wrapper passlyTable__status-wrapper-${getStatusColor(cellValue)}`}>{changeDisabledToInactive(cellValue)}</div>
                        </TableCell>
                    );
                case 'getComplexCell':
                    return <React.Fragment key={cellIndex}>{headerItem.getComplexCell(item, index)}</React.Fragment>;
                case 'customCheckbox':
                    return <React.Fragment key={cellIndex}>{headerItem.getCustomCheckboxCell(item, index, headerItem.id)}</React.Fragment>;
                default:
                    return (
                        <TableCell onClick={item.onClick} key={cellIndex}>
                            {cellValue}
                        </TableCell>
                    );
            }
        });
    };

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue && setSearchValue(e.target.value);
    };

    return (
        <div className={`passlyTable ${className}`}>
            {hideFilter || setSearchValue ? null : (
                <FilterBar
                    filterItems={filterItems}
                    onChange={handleFilterChange}
                    setFilters={handleSetFilters}
                    totalItemsCount={countBeforeFiltering}
                    currentCount={currentCountForFilter}
                    defaultSearchBy={defaultSearchBy}
                    hideCounter={hideCounter}
                />
            )}

            {/*TODO: remove when all search fields will be changed*/}
            {setSearchValue ? (
                <div className='passlyTable__search-block'>
                    <PasslyTextField
                        id='search-input'
                        className='passlyTable__search-input'
                        name='search'
                        label=''
                        value={searchValue}
                        onChange={handleFilterChange}
                        placeholder={translation('SEARCH_BY')}
                        fullWidth
                        showSearchIcon={true}
                    />
                </div>
            ) : null}
            {fixedHeader ? (
                <Table>
                    <TableHead>{getHeaderItems()}</TableHead>
                </Table>
            ) : null}
            <PasslyInfiniteScroll totalCount={rowsCount} currentCount={rows.length} onFetch={onFetch} resetLocal={resetLocal} height={height}>
                <Table>
                    {!fixedHeader ? <TableHead>{getHeaderItems()}</TableHead> : null}
                    <TableBody>
                        {rows?.map((row: any, index: string) => (
                            <TableRow
                                key={index}
                                className={`passlyTable__row ${row.checked ? 'passlyTable__row-active' : ''} ${
                                    row.onClick ? 'passlyTable__row-clickable' : ''
                                }`}
                            >
                                {getCheckboxItem(row, index)}
                                {showRowsNumbers ? getRowsNumbersCell(index) : null}
                                {getCells(row, index)}
                                {getRowMenuCell(row)}
                            </TableRow>
                        ))}
                        {loadingData && !rows.length ? (
                            <TableRow>
                                <TableCell className='passlyTable__row-loading' />
                            </TableRow>
                        ) : null}
                    </TableBody>
                </Table>
            </PasslyInfiniteScroll>

            {loadingData && <PageLoader useOverlay={true} isAbsolutePosition={true} />}

            {!loadingData && rows.length === 0 && getEmptyList()}
        </div>
    );
};
